import { useCallback, useEffect, useState } from 'react';
import { getDataSource } from '../lib/config';
import { userRolesService } from '../lib/database';
import { initialUserRoles } from '../data/userRoles';
import type { UserRole } from '../types';

interface UseUserRolesReturn {
  userRoles: UserRole[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (role: Omit<UserRole, 'id'> & { id?: string }) => Promise<UserRole>;
  update: (id: string, updates: Partial<Omit<UserRole, 'id'>>) => Promise<UserRole>;
  remove: (id: string) => Promise<void>;
}

export const useUserRoles = (enabled = true): UseUserRolesReturn => {
  const [userRoles, setUserRoles] = useState<UserRole[]>(initialUserRoles);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserRoles = useCallback(async () => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setUserRoles(initialUserRoles);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const roles = await userRolesService.getAll();
      if (roles.length > 0) {
        setUserRoles(roles);
      } else {
        // If no roles in Firestore, initialize them
        console.log('⚠️ No user roles found in Firestore, initializing default roles...');
        try {
          await userRolesService.initializeDefaultRoles();
          // Fetch again after initialization
          const initializedRoles = await userRolesService.getAll();
          setUserRoles(initializedRoles.length > 0 ? initializedRoles : initialUserRoles);
        } catch (initError) {
          console.error('❌ Error initializing roles:', initError);
          // Fall back to initial roles from code
          setUserRoles(initialUserRoles);
        }
      }
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user roles';
      console.error('❌ Error fetching user roles:', err);
      setError(errorMessage);
      // Fall back to initial roles from code
      setUserRoles(initialUserRoles);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    if (getDataSource() === 'mock') {
      void fetchUserRoles();
      return;
    }

    setLoading(true);
    setError(null);
    let initAttempted = false;

    const unsubscribe = userRolesService.subscribe((roles) => {
      if (roles.length > 0) {
        setUserRoles(roles);
        setLoading(false);
        return;
      }
      if (!initAttempted) {
        initAttempted = true;
        void (async () => {
          try {
            await userRolesService.initializeDefaultRoles();
          } catch (initError) {
            console.error('❌ Error initializing roles:', initError);
            setUserRoles(initialUserRoles);
          } finally {
            setLoading(false);
          }
        })();
        return;
      }
      setUserRoles(initialUserRoles);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [enabled, fetchUserRoles]);

  const createRole = useCallback(async (role: Omit<UserRole, 'id'> & { id?: string }) => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      const newRole: UserRole = {
        id: role.id || `role-${Date.now()}`,
        name: role.name,
        permissions: role.permissions,
      };
      setUserRoles((prev) => [...prev, newRole]);
      return newRole;
    }

    setError(null);
    try {
      const created = await userRolesService.create(
        { name: role.name, permissions: role.permissions },
        role.id
      );
      return created;
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create role';
      console.error('❌ Error creating user role:', err);
      setError(errorMessage);
      throw err;
    }
  }, []);

  const updateRole = useCallback(async (id: string, updates: Partial<Omit<UserRole, 'id'>>) => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      let updatedRole: UserRole | undefined;
      setUserRoles((prev) =>
        prev.map((role) => {
          if (role.id === id) {
            updatedRole = { ...role, ...updates };
            return updatedRole;
          }
          return role;
        })
      );
      if (!updatedRole) {
        throw new Error('Role not found');
      }
      return updatedRole;
    }

    setError(null);
    try {
      const updated = await userRolesService.update(id, updates);
      return updated;
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update role';
      console.error('❌ Error updating user role:', err);
      setError(errorMessage);
      throw err;
    }
  }, []);

  const removeRole = useCallback(async (id: string) => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setUserRoles((prev) => prev.filter((role) => role.id !== id));
      return;
    }

    setError(null);
    try {
      await userRolesService.delete(id);
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete role';
      console.error('❌ Error deleting user role:', err);
      setError(errorMessage);
      throw err;
    }
  }, []);

  return {
    userRoles,
    loading,
    error,
    refresh: fetchUserRoles,
    create: createRole,
    update: updateRole,
    remove: removeRole,
  };
};

